import Vue from 'vue';
Vue.config.ignoredElements = [
  /^og-/,
  'script',
  'og-when',
  'og-offer',
  'og-next-upcoming-order',
];
// Vue.config.ignoredElements = [/^og-next-upcoming-order/]

import { mapState, mapActions, mapGetters } from 'vuex';
import {
  decode,
  formatPrice,
  getBreakpoint,
  getStarsHTML,
  handleize,
  getValueForTag,
  decodeIDProductVariant,
  htmlDecode,
  getProductLimit,
} from 'utils/general';

import {
  addToCartEventData,
  pushDataPdpSpecifications,
  trackItemView,
  getSwatchChangeProductData,
  buildItemTrackingData,
  getLocalStorageProperties,
} from 'lib/tracking';
import YotpoReviewTracker from 'lib/yotpo-tracking';
import bus from 'lib/bus';
import productBundle from 'mixins/product-bundle';
import { getSellingPlan } from 'lib/ordergroove-helpers';
import datalayer from 'lib/datalayer';
import * as queries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import { trackEvent } from '../../assets/js/lib/tracking';

const isDesktopLayout = () => {
  return ['md', 'l'].includes(getBreakpoint());
};

export default {
  components: {
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
    modal: () => import('../modal/modal.vue'),
    'dropdown-custom': () => import('../dropdown-custom/dropdown-custom.vue'),
    'radio-group': () => import('../radio-group/radio-group.vue'),
    'product-notify-me-form': () =>
      import('../product-notify-me-form/product-notify-me-form.vue'),
    'pdp-related-product': () =>
      import('../pdp-related-product/pdp-related-product.vue'),
    'product-selector': () =>
      import('../product-selector/product-selector.vue'),
    'product-gallery': () => import('../product-gallery/product-gallery.vue'),
    accordion: () => import('../accordion/accordion.vue'),
    'product-gallery-mobile': () =>
      import('../product-gallery-mobile/product-gallery-mobile.vue'),
    'product-selector-group': () =>
      import('../product-selector-group/product-selector-group.vue'),
    'product-review-highlights': () =>
      import('../product-review-highlights/product-review-highlights.vue'),
    'quantity-selector': () =>
        import('../quantity-selector/quantity-selector.vue'),
  },
  props: [
    'productJson',
    'parse',
    'template',
    'collectionDisclaimer',
    'collectionCustomMessage',
    'collectionShippingMessage',
    'collectionShippingBanner',
    'collectionIcons',
    'productId',
  ],
  data() {
    return {
      orderType: this.defaultOrderType,
      showingStickyBar: false,
      isDesktopLayout: isDesktopLayout(),
      quantity: 1,
      productIcons: __GLOBAL__.productIcons,
      afterpayInterval: null,
      giftcardError: false,
      showPrice: true,
      sellingPlan: false,
      OGOptIn: false,
      sentDatalayerPush: false,
      price: 0,
      enableAddToCart: true,
      isRestrictedProduct: false,
      dyCtaTestEnabled: false,
      YotpoReviewTracker: null,
      tooltipActive: false,
      addToCartText: this.$t('products.product.add_to_cart'),
      pricingClass: 'product-hero__pricing-info OneLinkNoTx',
    };
  },
  mixins: [productBundle],
  created() {
    window.addEventListener('resize', this.resizeHandler);
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeHandler);
  },
  watch: {
    product() {
      if (this.product.id && !this.sentDatalayerPush) {
        datalayer.dataLayerPush(
          'eeProductDetail',
          getSwatchChangeProductData(this.product),
        );
        this.sentDatalayerPush = true;
      }
    },
  },
  computed: {
    ...mapGetters('global', ['productsWithSubscription']),
    ...mapState('global', ['cart', 'activeCountryCode', 'origin', 'listData']),
    ...mapState('product', [
      'product',
      'metafields',
      'settings',
      'reviews',
      'variant',
      'subscription',
    ]),
    ...mapState('cart', ['productsData', 'cartInProgress']),
    productObject() {
      return this.parse
        ? JSON.parse(decode(this.productJson))
        : this.productJson;
    },
    variantIDClean() {
      const res = this.variant.id
        ? this.variant.id.includes('gid:')
          ? decodeIDProductVariant(this.variant.id)
          : this.variant.id
        : false;

      if (res) {
        this.configureSubscription();
      }
      return res;
    },
    variantTitle() {
      return this.getVariantTitle(this.productObject.title);
    },
    bundleButtonText() {
      return this.enableAddToCart && !this.isRestrictedProduct
        ? this.$t('products.product.add_to_cart_bundle')
        : this.$t('products.product.sold_out');
    },
    variantPrice() {
      if (!this.variant.price) return '';

      let price = this.variant.price;
      if (this.variant.compareAtPrice && !this.productObject.isBundle) {
        price = this.variant.compareAtPrice;
      }
      return formatPrice(price, true, true);
    },
    variantAvailable() {
      return this.variant.available;
    },
    variantDiscountPrice() {
      if (
        !this.variant.price ||
        this.productObject.isBundle ||
        (!this.variant.compareAtPrice && !this.OGOptIn)
      )
        return false;
      if (this.OGOptIn && !this.variant.compareAtPrice) {
        const subscriptionPrice = this.getSubscriptionPrice(
          this.product,
          this.variant,
        );

        return formatPrice(subscriptionPrice, true, true);
      }
      return formatPrice(this.variant.price, true, true);
    },
    badges() {
      if (!this.product.tags) return [];

      return this.product.tags
        .filter((tag) => {
          return tag.includes('badge|');
        })
        .map((badge) => {
          const text = badge.replace('badge|', '');
          return {
            class: `fenty-badge-${handleize(text)}`,
            text: text,
          };
        });
    },
    stars() {
      if (!this.productObject.reviewsAverage) return '';
      return getStarsHTML(this.productObject.reviewsAverage);
    },
    starsAriaLabel() {
      if (!this.productObject.reviewsAverage) return '';
      return `${this.productObject.reviewsAverage} out of 5 stars based on ${this.productObject.reviewsCount} reviews`;
    },
    rating() {
      if (!this.productObject.reviewsCount) return '';
      return this.productObject.reviewsCount;
    },
    readReviewsLink() {
      if (!this.productObject.reviewsCount) return '';
      return `${this.productObject.reviewsCount} Review${
        this.productObject.reviewsCount > 1 ? 's' : ''
      }`;
    },
    installmentPaymentsText() {
      const payment_count = window.location.hostname.split(`.`).pop() === `uk` ? 3 : 4;
      const text = this.$t('installment_payments.text_content');
      const price = this.variant.price;
      const formattedPrice = formatPrice(Math.floor(price / payment_count));
      return text.replace('{payment_count}', payment_count).replace('{price}', formattedPrice);
    },
    customMessage() {
      return (
        this.metafields?.general?.custom_message || this.collectionCustomMessage
      );
    },
    shippingMessage() {
      return (
        this.metafields?.general?.shipping_message ||
        this.collectionShippingMessage
      );
    },
    decodedShippingBanner() {
      return typeof this.collectionShippingBanner === 'string'
        ? JSON.parse(decode(this.collectionShippingBanner))
        : this.collectionShippingBanner;
    },
    disclaimer() {
      return this.metafields?.general?.disclaimer || this.collectionDisclaimer;
    },
    shippingDelayText() {
      return this.metafields?.general?.shipping_delay_text;
    },
    icons() {
      if (this.metafields?.general?.icons) {
        const productIcons = JSON.parse(this.metafields.general.icons);
        if (productIcons.references.length > 0) {
          return JSON.parse(this.metafields.general.icons)
            .references.map((icon) => this.productIcons[icon.index])
            .filter(Boolean);
        }
      }
      if (this.collectionIcons) {
        return JSON.parse(this.collectionIcons)
          .references.map((icon) => this.productIcons[icon.index])
          .filter(Boolean);
      }
      return false;
    },
    countsInCart() {
      let countsInCart = 0;
      for (const handle in this.productsData) {
        if (this.productsData[handle]?.tags?.includes('gift card')) {
          countsInCart = this.productsData[handle].quantity;
        }
      }

      if (countsInCart === 0) {
        this.giftcardError = false;
      }
      return countsInCart;
    },
    showingSubscription() {
      // TOOD Add a condition to check if current product has a subscription
      return this.activeCountryCode === 'US' || this.activeCountryCode === 'GB';
    },
    hasPreorder() {
      if (this.product?.tags) {
        return this.product?.tags.includes('preorder');
      }

      return false;
    },
    shortDescription() {
      if (
        this.variant.shortDescription &&
        this.variant.shortDescription !== 'false'
      ) {
        if (this.variant.shortDescription.value) {
          return this.variant.shortDescription.value;
        }
        return this.variant.shortDescription;
      }
      if (
        this.product?.metafields?.general?.short_description &&
        this.product?.metafields?.general?.short_description !== 'false'
      ) {
        return this.product?.metafields?.general?.short_description;
      }
      if (
        this.productObject.collectionShortDescription &&
        this.productObject.collectionShortDescription !== 'false'
      ) {
        return this.productObject.collectionShortDescription;
      }
      return false;
    },
    productLimit() {
      return getProductLimit(this.productObject.tags) || 5;
    },
    maxQty() {
      const sameIdProducts = this.cart.items.filter(
        (item) => item.product_id === Number(this.product.id),
      );
      const sameIdProductsQuantity = sameIdProducts.reduce(
        (acc, item) => acc + item.quantity,
        0,
      );
      const quantity = this.productLimit - sameIdProductsQuantity;
      return quantity;
    },
    limitText() {
      return this.$t('products.product.max_warning', {
        quantity: this.productLimit,
      });
    },
  },
  async mounted() {
    bus.$on('update-variant', this.handleVariantChange);
    bus.$on('cart-refreshed', this.refreshBundleAvailability);
    bus.$on(
      'product-card-slider:navigation',
      this.handleSliderNavigationClicked,
    );
    const pdpLoadedEvent = new CustomEvent('product-hero:loaded');
    window.dispatchEvent(pdpLoadedEvent);

    const params = new URLSearchParams(window.location.search);
    const section = params.get('section');
    if (section) {
      bus.$emit(`open-${section}-accordion`);
      setTimeout(() => {
        this.$scrollTo('#reviews', 0, { offset: -150 });
      }, 300);
    }

    if (this.productObject) {
      this.updateProduct(this.productObject.handle).then(() => {
        let currentVariantObject = this.product.variants[0];
        const currentVariantId = params.get('variant');

        if (currentVariantId) {
          currentVariantObject = this.product.variants.find(
            (variant) => variant.id === currentVariantId,
          );
        }
        this.updateVariant(currentVariantObject);
        bus.$emit('update-variant', currentVariantObject);
      });

      this.YotpoReviewTracker = new YotpoReviewTracker(this.variantTitle);

      if (this.productObject.bundled_products.length) {
        this.isBundle = true;
        await this.fetchBundleProductsByHandles(
          this.productObject.bundled_products,
        );
        this.refreshBundleAvailability();
        this.checkBundleAvailability();
        this.pricingClass =
          'product-hero__pricing-info OneLinkNoTx product-hero__pricing-info--bundle';
      } else {
        this.checkReplacementProduct(this.productObject);
      }
      this.getTrackingItemViewData();
    }

    this.afterpayInterval = setInterval(() => {
      if (typeof Afterpay !== 'undefined') {
        if (
          (this.variant.available && this.variant.id) ||
          (!this.variant.id && this.variantPrice)
        ) {
          clearInterval(this.afterpayInterval);
          Afterpay.createPlacements({
            targetSelector: '.product-hero__shipping-info__afterpay',
            attributes: {
              currency: 'USD',
              locale: 'en_US',
              amountSelector: this.variant.compareAtPrice
                ? '.product-hero__discount-price'
                : '.product-hero__price',
            },
          });
        }
      }
    }, 1000);

    this.handleSubscriptionDetailClickTracking();

    if (this.$refs.promoBanner) {
      this.$nextTick(() => {
        const promoBannerOpenChat = this.$refs.promoBanner.querySelector('[data-promo-banner-open-chat]').value == 'true';
        if (promoBannerOpenChat) {
          const _this = this;
          setTimeout(function() {
            const promoBannerLink = _this.$refs.promoBanner.querySelector('a');
            if (promoBannerLink) {
              promoBannerLink.addEventListener('click', function(e) {
                e.preventDefault();
                if (typeof GorgiasChat !== 'undefined' && typeof GorgiasChat.open === 'function') {
                  try {
                    GorgiasChat.open();
                  } catch (error) {
                    console.error('Gorgias Chat error:', error);
                  }
                }
              })
            }
          }, 500);
        }
      });
    }
  },
  methods: {
    ...mapActions('global', ['openModal', 'updateBackInStock']),
    ...mapActions('cart', [
      'addBundleItemsToCart',
      'updateCartLine',
      'addToCart',
      'openCart',
      'closeCart',
      'updateChangePending',
      'updateCartInProgress',
    ]),
    ...mapActions('product', [
      'updateProduct',
      'updateVariant',
      'updateProductStatic',
      'updatePrice',
      'updateAvailable',
      'updateOption',
    ]),
    ...mapActions('product', ['updateReviews']),
    getDynamicSubscriptionId(variant, sellingPlan) {
      const frequency = sellingPlan.options[0].value.split(' ');
      let result = '';

      if (frequency.length === 1) {
        const frequencyPeriod = frequency[0]
          .toUpperCase()
          .replace('DAY', '1')
          .replace('WEEK', '2')
          .replace('MONTH', '3');
        const formattedFrequency = `1_${frequencyPeriod}`;
        result = `og-selling-plan-map-${variant.id}-${formattedFrequency}`;
        return result;
      }

      const frequencyPeriod = frequency[1]
        .replace('(s)', '')
        .replace('s', '')
        .toUpperCase()
        .replace('DAY', '1')
        .replace('WEEK', '2')
        .replace('MONTH', '3');
      const formattedFrequency = `${frequency[0]}_${frequencyPeriod}`;
      result = `og-selling-plan-map-${variant.id}-${formattedFrequency}`;
      return result;
    },
    handleAccountTracking(event) {
      const { srcElement } = event;
      if (srcElement.tagName == 'A') {
        if (srcElement.title == 'login' || srcElement.textContent == 'Log in') {
          trackEvent({
            event: 'login_start',
            component_name: 'pdp',
            module_name: 'unlock free shipping',
            shopify_module_name: 'product hero'
          });
        } else if (srcElement.title == 'register' || srcElement.textContent == 'Create an account') {
          trackEvent({
            event: 'sign_up_start',
            component_name: 'pdp',
            module_name: 'unlock free shipping',
            shopify_module_name: 'product hero'
          });
        }
      }
    },
    handleSubscriptionDetailClickTracking() {
      const thisObj = this;
      const observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(function (node) {
              if (node.tagName === 'OG-WHEN') {
                const detailBtn = node.querySelector('.og-offer-header__link');

                if (detailBtn) {
                  detailBtn.addEventListener('click', function (e) {
                    trackEvent({
                      event: 'product_module_interaction',
                      click_action: 'click',
                      product_name: thisObj.product.title,
                      module_name: 'subscription details',
                      shopify_module_name: 'product hero'
                    });
                  });
                  observer.disconnect();
                }
              }
            });
          }
        });
      });

      // Start observing for changes in the DOM
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    },
    handleVariantChange(variant) {
      this.updateUrl(variant);
    },
    handleSliderNavigationClicked({ direction, moduleName }) {
      window.dataLayer.push({
        event: 'product_module_interaction',
        module_name: moduleName.replace('-', ''),
        scroll_direction: direction,
        click_action: 'scroll',
        product_name: this.variantTitle.trim(),
        shopify_module_name: 'product hero'
      });
    },
    updateUrl(variant) {
      const variantId = variant.id.includes('ProductVariant')
        ? variant.id.split('ProductVariant/').pop()
        : variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.href);
    },
    getSubscriptionPrice(product, variant) {
      const tagDiscount = getValueForTag(
        'ordergroove_discount',
        product.tags,
        '::',
      );

      const discount = tagDiscount || __GLOBAL__.subscriptionDiscount || 0;
      const subscriptionPrice = Math.round(
        variant.price * (1 - discount / 100),
      );
      return subscriptionPrice;
    },
    toggle(obj) {
      if (obj.isOpen === 'visible') {
        pushDataPdpSpecifications({
          productTitle: obj.product.title,
          listTitle: obj.title,
        });
      }
    },
    setupShippingCalloutTest() {
      if (
        this.activeCountryCode &&
        (this.activeCountryCode !== 'US' || this.activeCountryCode !== 'GB')
      )
        return false;
      const detailsCTAEl = this.$refs.addToCartButtonWrapper;
      const shippingText = this.$t('products.product.shipping_callout');
      const shippingCalloutElement = document.createElement('div');
      shippingCalloutElement.className = 'product-hero__shipping-callout';
      shippingCalloutElement.innerHTML = `<p>${shippingText}</p>`;
      detailsCTAEl.append(shippingCalloutElement);
    },
    configureSubscription() {
      const OGIntervalMaxRetry = 10;
      let OGIntervalCurrentRetry = 0;
      const OGInterval = setInterval(() => {
        if (window.OG) {
          clearInterval(OGInterval);
          this.OGOptIn = false;
          window.OGOptIn = false;
          if (
            OG.getOptins().find(
              (optIn) => +optIn.product === +this.variantIDClean,
            )
          ) {
            this.OGOptIn = true;
            window.OGOptIn = true;
          }
          OG.addOptinChangedCallback((e) => {
            this.OGOptIn = e.optedIn;
            window.OGOptIn = e.optedIn;
          });
        }
        OGIntervalCurrentRetry++ > OGIntervalMaxRetry &&
          clearInterval(OGInterval);
      }, 300);
    },
    getVariantTitle(t) {
      return t.split(window.__GLOBAL__.productDelimiter)[0].trim();
    },
    resizeHandler() {
      this.isDesktopLayout = isDesktopLayout();
    },
    openSizeChart() {
      this.openModal('sizeChart');
    },
    onOrderTypeInputChange({ value }) {
      this.orderType = value;
    },
    getInitialOrderTypeValue() {
      return this.orderType;
    },
    addProduct() {
      const buttonFocused = this.$el.querySelector('.product-hero__add-cta');

      if (this.template === 'product.gift') {
        if (this.hasGiftCard()) {
          this.giftcardError = true;
          return false;
        }
      }

      this.updateCartInProgress(true);

      const { plan, frequency } = getSellingPlan();

      // Testing
      // this.sellingPlan = 421757061

      const callback = () => {
        setTimeout(() => {
          this.closeCart();
          this.updateChangePending(false);
         
          if (buttonFocused) {
            buttonFocused.focus();
            buttonFocused.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 2500);
      };

      if (this.productsWithSubscription.length > 0 || plan) {
        const variantId = parseInt(this.variantIDClean, 10);
        const productInCartWithSubscription =
          this.productsWithSubscription.find((p) => p.id === variantId);
        if (productInCartWithSubscription || plan) {
          this.updateChangePending(true);
          const properties = {
            _product_collection: this.productObject.categories[0],
            _product_tags: this.product.tags,
            _upsell_bundle: this.metafields?.general?.cart_upsell_bundle,
            _promo_text: this.metafields?.cart_promo?.promo_text,
            _promo_link: this.metafields?.cart_promo?.link || '',
            _product_rating: this.productObject.reviewsAverage,
            _subscription_frequency: frequency,
            ...getLocalStorageProperties(),
          };

          this.updateCartLine({
            quantity: 0,
            id: variantId,
            callback: () => {
              this.addToCart({
                variant: this.variant,
                quantity: this.quantity,
                subscription: plan,
                callback,
                properties,
                moduleName: 'product hero'
              });
            },
          });
          const extraData = this.getExtraData();
          extraData.subscriptionPrice = this.getSubscriptionPrice(
            this.product,
            this.variant,
          );
          datalayer.dataLayerPush(
            'addToCart',
            addToCartEventData(this.productObject, this.quantity, extraData),
          );
          return;
        }
      }

      let properties = null;
      if (this.hasPreorder) {
        properties = {
          ...properties,
          _Preorder: 'true',
        };
      }

      properties = {
        ...properties,
        _product_collection: this.productObject.categories[0],
        _product_tags: this.product.tags,
        _product_rating: this.productObject.reviewsAverage,
        _upsell_bundle: this.product.metafields?.general?.cart_upsell_bundle,
        _promo_text: this.metafields?.cart_promo?.promo_text,
        _promo_link: this.metafields?.cart_promo?.link || '',
        ...(plan && { subscription_frequency: frequency }),
        ...getLocalStorageProperties(),
      };

      if (this.isBundle) {
        this.addBundleToCart({
          price: this.variant.price,
          title: this.variantTitle,
          productObject: this.productObject,
          quantity: this.quantity,
          subscription: plan,
          callback,
          properties,
        });
      } else {
        if (this.maxQty > 0) {
          const addToCartQuantity = Math.min(this.quantity, this.maxQty);
          this.addToCart({
            variant: this.variant,
            quantity: addToCartQuantity,
            subscription: plan,
            callback,
            properties,
            moduleName: 'product hero'
          });
        }
      }
      const extraData = this.getExtraData();
      datalayer.dataLayerPush(
        'addToCart',
        addToCartEventData(this.productObject, this.quantity, extraData),
      );
    },
    updateQuantity(quantity) {
      this.quantity = quantity;
      if (this.isBundle) {
        this.refreshBundleAvailability();
      }
    },
    openNotifyModal() {
      this.updateBackInStock({ product: this.product, variant: this.variant });
      this.openModal('back-in-stock');
    },
    getExtraData() {
      let addToCartType = 'Home';
      if (window.location.pathname.indexOf('/collections') > -1) {
        addToCartType = 'Product List Page';
        if (window.location.pathname.indexOf('/products') > -1) {
          addToCartType = 'Product Detail Page';
        }
      } else if (window.location.pathname.indexOf('/cart') > -1) {
        addToCartType = 'Cart';
      } else if (window.location.pathname.indexOf('/products') > -1) {
        addToCartType = 'Product Detail Page';
      }
      return {
        addToCartType,
        sku: this.variant.sku,
      };
    },
    decodeHtml(html) {
      var txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    },
    async updateBundle({ product, key }) {
      this.bundleProductsData[parseInt(key)] = {
        ...product,
      };
      this.refreshBundleAvailability();
      this.getBundleFullPrice();

      product.title = this.decodeHtml(product.title);
    },
    refreshBundleAvailability() {
      if (!this.isBundle) return;
      if (this.isBundleUnavailable()) {
        this.enableAddToCart = false;
      } else {
        this.enableAddToCart = true;
      }
    },
    checkBundleAvailability() {
      if (!this.isBundle) return;
      this.isRestrictedProduct = false;

      const bundleRestrictedProducts = this.bundleProductsData.filter(
        (product) =>
          (product.tags ?? []).some((tag) =>
            tag.includes('shipping-restricted-replacement'),
          ),
      );

      if (!bundleRestrictedProducts.length) return;

      bundleRestrictedProducts.forEach((product) => {
        this.checkReplacementProduct(product);
      });
    },
    async checkReplacementProduct(product) {
      const restrictedProductTag = product.tags?.find((tag) =>
        tag.includes(`shipping-restricted-replacement|${window.shippingCode}`),
      );
      if (restrictedProductTag) {
        const restrictedProductId = restrictedProductTag.split(
          `shipping-restricted-replacement|${window.shippingCode}|`,
        )[1];
        const restrictedProduct = await this.getRestrictedProductById(
          restrictedProductId,
        );
        if (!restrictedProduct.node) {
          console.warn('No replacement for the restricted product was found');
          this.isRestrictedProduct = true;
          this.enableAddToCart = false;
          this.addToCartText = this.$t('products.product.sold_out');
        }
      }
    },
    async getRestrictedProductById(id) {
      const response = await storefront.request(
        queries.productAvailabilityByVariantId,
        {
          id: 'gid://shopify/ProductVariant/' + id,
        },
      );
      return response;
    },
    openReviews() {
      bus.$emit('open-reviews-accordion');
    },
    hasGiftCard() {
      let hasGiftCard = false;
      for (const handle in this.productsData) {
        if (this.productsData[handle].tags.includes('gift card')) {
          hasGiftCard = true;
        }
      }
      return hasGiftCard;
    },
    handleClickSendGift(e) {
      if (this.countsInCart) {
        this.giftcardError = true;
      } else {
        this.giftcardError = false;
      }
    },
    async getTrackingItemViewData() {
      const otherData = {
        variant: this.productObject.variants[0]
          ? this.productObject.variants[0].title
          : '',
      };
      if (this.isBundle) {
        otherData.bundleName = this.productObject.title;
        otherData.bundleId = this.productObject.id;
        otherData.bundleProducts = this.bundleProductsData;
      }

      trackItemView(this.productObject, otherData, 'Product Detail Page');
    },
    showTooltipContent() {
      this.tooltipActive = true;
    },
    hideTooltipContent() {
      this.tooltipActive = false;
    },
    checkFocus(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.hideTooltipContent();
      }
    },
  },
};
